import React, { useState, useEffect } from "react";
import {
  Box, Card, CardContent, Typography,
  Table, TableBody, TableCell, TableHead, TableRow,
  Avatar, IconButton, Pagination, Popover,
  TextField, InputAdornment, TableContainer,
  Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, List, ListItem,
  ListItemIcon, ListItemText, Chip,
  MenuItem, Select, FormControl, InputLabel,
  Snackbar, Alert, Tabs, Tab, Rating,
  Grid, alpha, Checkbox, CircularProgress
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import no_user from "../assets/no_user.png";
import nodatafound from "../assets/nodatafound.png";
import "bootstrap-icons/font/bootstrap-icons.css";
import {
  SupportAgent as SupportAgentIcon,
  Verified as VerifiedIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  WarningAmber,
  FilterList,
  DirectionsCar,
  TwoWheeler,
  LocalShipping,
  ContentCopy,
  Visibility,
  ArrowUpward,
  ArrowDownward,
  Refresh as RefreshIcon,
  Tune as TuneIcon,
  FilterAltOff as FilterOffIcon,
  AirportShuttle, CommuteOutlined, DescriptionOutlined,
  TrendingUp, Gavel, WbSunny, LocalFireDepartment,
  SwapHoriz, DirectionsBus,
  PersonAdd as PersonAddCreateIcon
} from "@mui/icons-material";
import { PersonAddAlt1 as PersonAddIcon } from "@mui/icons-material";
import formatNumber from "../utils/formatNumber";
import CustomerDetailsView from "./CustomerDetailsView";
import { getAllUsers, assignCustomerToUser } from "../services/customerApi";

export default function CustomerLeadModule({
  users,
  searchTerm,
  setSearchTerm,
  page,
  setPage,
  filteredUsers,
  handleEditUser,
  handleDeleteUser,
  searchColumn,
  setSearchColumn,
  statusFilter,
  setStatusFilter,
  getUserStatus,
  setActiveSection,
  handleOpenRegistration,
  loggedUserType,
  handleEnquirySearch,
  filteredEnqUsers,
  expiryFilter,
  setExpiryFilter,
  activeTab,
  setActiveTab,
  onRefreshCustomers
}) {
  const [openFileDialog, setOpenFileDialog] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [viewDetailsDialog, setViewDetailsDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [fileViewer, setFileViewer] = useState(null); // { url, name, type }
  const [daysToExpire, setDaysToExpire] = useState("30");
  const [schedStart, setSchedStart] = useState("");
  const [schedEnd, setSchedEnd] = useState("");
  const [endStart, setEndStart] = useState("");
  const [endEnd, setEndEnd] = useState("");
  const [filterRating, setFilterRating] = useState(0);
  const [filterVerification, setFilterVerification] = useState("all");
  const [filterPopoverAnchor, setFilterPopoverAnchor] = useState(null);
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState("all");
  const [assignedToFilter, setAssignedToFilter] = useState("all");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sortOrder, setSortOrder] = useState("asc");

  // ── Assignment state (admin-only) ──────────────────────────────────────────
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [assignUsers, setAssignUsers] = useState([]);
  const [selectedAssignUser, setSelectedAssignUser] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);

  const [enquiryForm, setEnquiryForm] = useState({
    name: "",
    phone: "",
    vehicleNumber: ""
  });

  // Reset page on filter change
  useEffect(() => {
    setPage(0);
  }, [searchTerm, statusFilter, vehicleTypeFilter, filterRating, filterVerification, schedStart, schedEnd, endStart, endEnd, sortOrder, assignedToFilter]);

  // Fetch assignable users (EMPLOYEE + MARKETER) for admin
  useEffect(() => {
    if (loggedUserType === "ADMIN") {
      getAllUsers()
        .then(data => {
          const filtered = (data || []).filter(
            u => u.role === "EMPLOYEE" || u.role === "MARKETER"
          );
          setAssignUsers(filtered);
        })
        .catch(err => console.error("Failed to load users", err));
    }
  }, [loggedUserType]);

  // Clear row selection when page changes
  useEffect(() => {
    setSelectedRows(new Set());
    setSelectedAssignUser("");
  }, [page]);

  // ── Row checkbox helpers ───────────────────────────────────────────────────
  const isRowSelected = (id) => selectedRows.has(id);

  const handleToggleRow = (id) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // handleSelectAll / allCurrentSelected / someCurrentSelected are defined
  // AFTER displayedUsers (below) to avoid the TDZ ReferenceError.

  // ── Assign handler ────────────────────────────────────────────────────────
  const handleAssignCustomers = async () => {
    if (selectedRows.size === 0 || !selectedAssignUser) return;
    try {
      setAssignLoading(true);
      const customerIds = Array.from(selectedRows);
      const selectedUserObj = assignUsers.find(u => u.id === selectedAssignUser);
      const userName = selectedUserObj ? (selectedUserObj.userName || selectedUserObj.name) : "";
      await assignCustomerToUser(customerIds, selectedAssignUser, userName);
      setSnackbar({
        open: true,
        message: `${customerIds.length} customer(s) assigned successfully!`,
        severity: "success"
      });
      setSelectedRows(new Set());
      setSelectedAssignUser("");
      // Refresh the customer list so the updated "Assigned" column is visible
      if (typeof onRefreshCustomers === "function") {
        onRefreshCustomers();
      }
    } catch (err) {
      console.error("Assignment failed", err);
      setSnackbar({
        open: true,
        message: "Failed to assign customers. Please try again.",
        severity: "error"
      });
    } finally {
      setAssignLoading(false);
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setSnackbar({ open: true, message: "Data refreshed successfully!", severity: "success" });
      setIsRefreshing(false);
    }, 1000);
  };

  const getStatusText = (endDate) => {
    if (!endDate) return "No Date";
    const end = new Date(endDate);
    const today = new Date();
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return "Expired";
    const threshold = expiryFilter === "All" ? 30 : parseInt(expiryFilter || "30");
    if (diffDays <= threshold) return "Expiring Soon";
    return "Active";
  };

  const getUserAggregateStatus = (user) => {
    const vehicles = user.vehicles || [];
    const dates = vehicles.map(v => v.endDate).filter(d => !!d);
    if (user.endDate) dates.push(user.endDate);
    if (dates.length === 0) return "Active";
    const statuses = dates.map(d => getStatusText(d));
    if (statuses.includes("Expired")) return "Expired";
    if (statuses.includes("Expiring Soon")) return "Expiring Soon";
    return "Active";
  };

  const getUserAggregateColor = (user) => {
    const status = getUserAggregateStatus(user);
    if (status === "Expired") return "error";
    if (status === "Expiring Soon") return "warning";
    return "success";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not Set";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) { return "Invalid Date"; }
  };

  const getFilteredUsersForDisplay = () => {
    return filteredEnqUsers.filter(user => {
      // Column Search
      let matchesSearch = true;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        if (searchColumn === "all") {
          matchesSearch = (user.name || "").toLowerCase().includes(term) ||
            (user.phone || "").toLowerCase().includes(term) ||
            (user.email || "").toLowerCase().includes(term) ||
            (user.location || "").toLowerCase().includes(term) ||
            (user.vehicles || []).some(v =>
              (v.number || "").toLowerCase().includes(term) ||
              (v.scheduleDate || "").toLowerCase().includes(term)
            );
        } else if (searchColumn === "name") {
          matchesSearch = (user.name || "").toLowerCase().includes(term);
        } else if (searchColumn === "phone") {
          matchesSearch = (user.phone || "").toLowerCase().includes(term);
        } else if (searchColumn === "vehicleNumber") {
          matchesSearch = (user.vehicles || []).some(v => (v.number || "").toLowerCase().includes(term));
        } else if (searchColumn === "scheduleDate") {
          matchesSearch = (user.vehicles || []).some(v => (v.scheduleDate || "").toLowerCase().includes(term));
        }
      }

      const matchStatus = statusFilter === "all" || getUserAggregateStatus(user).toLowerCase() === statusFilter.toLowerCase();
      const matchVehicle = vehicleTypeFilter === "all" || (user.vehicles && user.vehicles.some(v => (v.type || "two_wheeler").toLowerCase() === vehicleTypeFilter.toLowerCase()));

      let matchSched = true;
      if (schedStart || schedEnd) {
        const start = schedStart ? new Date(schedStart) : null;
        if (start) start.setHours(0, 0, 0, 0);
        const end = schedEnd ? new Date(schedEnd) : null;
        if (end) end.setHours(23, 59, 59, 999);
        matchSched = (user.vehicles || []).some(v => {
          const d = v.scheduleDate ? new Date(v.scheduleDate) : null;
          return d && (!start || d >= start) && (!end || d <= end);
        });
      }

      let matchEnd = true;
      if (endStart || endEnd) {
        const start = endStart ? new Date(endStart) : null;
        if (start) start.setHours(0, 0, 0, 0);
        const end = endEnd ? new Date(endEnd) : null;
        if (end) end.setHours(23, 59, 59, 999);
        matchEnd = (user.vehicles || []).some(v => {
          const d = v.endDate ? new Date(v.endDate) : null;
          return d && (!start || d >= start) && (!end || d <= end);
        });
      }

      const matchRating = filterRating === 0 || (user.rating >= filterRating);
      const matchVerification = filterVerification === "all" || (user.verification === filterVerification);

      const matchAssignedTo = assignedToFilter === "all" ||
        (assignedToFilter === "unassigned" && !user.assignedToWho && !user.assignedTo) ||
        (user.assignedTo === assignedToFilter) ||
        (user.assignedToWho === assignedToFilter);

      return matchesSearch && matchStatus && matchVehicle && matchSched && matchEnd && matchRating && matchVerification && matchAssignedTo;
    });
  };

  const sortedUsers = [...getFilteredUsersForDisplay()].sort((a, b) => {
    const nameA = (a.name || "").trim();
    const nameB = (b.name || "").trim();
    return sortOrder === "asc"
      ? nameA.localeCompare(nameB, undefined, { sensitivity: "base" })
      : nameB.localeCompare(nameA, undefined, { sensitivity: "base" });
  });

  const displayedUsers = sortedUsers.slice(page * 5, (page + 1) * 5);

  // ── Select-all helpers ────────────────────────────────────────────────────
  // "Select All" selects EVERY record in the full filtered list (sortedUsers),
  // not just the current page slice (displayedUsers).
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRows(new Set(sortedUsers.map(u => u.id)));
    } else {
      setSelectedRows(new Set());
    }
  };

  // Header checkbox is "checked" only when ALL filtered records are selected.
  const allCurrentSelected =
    sortedUsers.length > 0 &&
    sortedUsers.every(u => selectedRows.has(u.id));

  // Header checkbox is "indeterminate" when some (but not all) are selected.
  const someCurrentSelected =
    sortedUsers.some(u => selectedRows.has(u.id)) && !allCurrentSelected;

  const handleOpenFiles = (files) => {
    setSelectedFiles(files || []);
    setOpenFileDialog(true);
  };

  const handleCloseFiles = () => {
    setSelectedFiles([]);
    setOpenFileDialog(false);
  };

  const handlePreview = (file) => {
    if (!file || !file.url) return;
    setFileViewer({ url: file.url, name: file.name, type: file.type });
  };

  const handleCloseFileViewer = () => setFileViewer(null);

  const handleDownloadFile = () => {
    if (!fileViewer) return;
    // Open the file in a new window/tab.
    // cross-origin URLs (S3) ignore the `download` attribute so we use
    // window.open which always opens a new window, never the current page.
    window.open(fileViewer.url, "_blank", "noopener,noreferrer");
  };

  const handleOpenDelete = (id) => {
    setDeleteUserId(id);
    setOpenDeleteDialog(true);
  };

  const handleCloseDelete = () => {
    setDeleteUserId(null);
    setOpenDeleteDialog(false);
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setViewDetailsDialog(true);
  };

  const getVehicleIcon = (type) => {
    switch ((type || "two_wheeler")?.toLowerCase()) {
      case "two_wheeler": return <TwoWheeler fontSize="small" />;
      case "private_car": return <DirectionsCar fontSize="small" />;
      case "passengers_vehicle": return <AirportShuttle fontSize="small" />;
      case "goods_vehicle": return <LocalShipping fontSize="small" />;
      case "miscellaneous_vehicle": return <CommuteOutlined fontSize="small" />;
      case "non_motor": return <DescriptionOutlined fontSize="small" />;
      case "idv_increase": return <TrendingUp fontSize="small" />;
      case "hp_endorsement": return <Gavel fontSize="small" />;
      case "solar_policy": return <WbSunny fontSize="small" />;
      case "fire_policy": return <LocalFireDepartment fontSize="small" />;
      case "name_transfer": return <SwapHoriz fontSize="small" />;
      case "school_bus": return <DirectionsBus fontSize="small" />;
      default: return <DirectionsCar fontSize="small" />;
    }
  };

  const getVehicleCountByType = (vehicles, type) => {
    if (!vehicles) return 0;
    return vehicles.filter(v => v.type?.toLowerCase() === type.toLowerCase()).length;
  };

  const resetAllFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setVehicleTypeFilter("all");
    setAssignedToFilter("all");
    setSearchColumn("all");
    setFilterRating(0);
    setFilterVerification("all");
    setSchedStart("");
    setSchedEnd("");
    setEndStart("");
    setEndEnd("");
    setPage(0);
    if (setExpiryFilter) setExpiryFilter("All");
  };

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, pt: 0, fontFamily: "'Outfit', sans-serif", backgroundColor: "#0a0a0a", minHeight: "100vh" }}>
      {/* Back Navigation + Create Customer */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography
          onClick={handleOpenRegistration}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            cursor: "pointer",
            fontFamily: "'Poppins', sans-serif",
            color: "#ffffff",
            fontWeight: 500,
            "&:hover": { color: "#FDB813", textDecoration: "underline" }
          }}
        >
          <ArrowBackIcon sx={{ mr: 1, fontSize: "1.2rem" }} />
          Back
        </Typography>

        <Button
          variant="contained"
          startIcon={<PersonAddCreateIcon />}
          onClick={handleOpenRegistration}
          sx={{
            bgcolor: "#FDB813",
            color: "#111",
            fontWeight: 700,
            fontFamily: "'Poppins', sans-serif",
            borderRadius: "10px",
            px: 2.5,
            py: 1,
            whiteSpace: "nowrap",
            textTransform: "none",
            boxShadow: "0 4px 16px rgba(253, 184, 19, 0.3)",
            "&:hover": {
              bgcolor: "#e5a800",
              boxShadow: "0 6px 24px rgba(253, 184, 19, 0.45)",
            }
          }}
        >
          Create Customer
        </Button>
      </Box>

      {/* Header Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "rgba(255, 255, 255, 0.1)", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          TabIndicatorProps={{ style: { backgroundColor: "#FDB813" } }}
          sx={{
            "& .MuiTab-root": {
              color: "rgba(255, 255, 255, 0.5)",
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 600,
              textTransform: "none",
              fontSize: "1rem"
            },
            "& .Mui-selected": { color: "#FDB813 !important" }
          }}
        >
          <Tab label="Customer Management" />
          <Tab label="Your follow-up customer" />
        </Tabs>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ display: "block" }}>
        <Card sx={{ borderRadius: { xs: 2, md: 3 }, background: "#111111", border: "1px solid rgba(253, 184, 19, 0.1)", boxShadow: "0 20px 60px rgba(0,0,0,0.6)" }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>

            {/* Minimalist Filter Hub Toolbar */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
              <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', color: '#ffffff', fontWeight: 800, fontFamily: "'Outfit', sans-serif" }}>
                <SupportAgentIcon fontSize="large" sx={{ mr: 1, color: activeTab === 0 ? "#FDB813" : "#4caf50" }} />
                {activeTab === 0 ? "Customer Management" : "Your follow-up customer"}
              </Typography>

              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <Tooltip title="Refresh">
                  <IconButton onClick={handleRefresh} disabled={isRefreshing} sx={{ borderRadius: "10px", border: "1px solid rgba(255, 255, 255, 0.1)", color: "#fff" }}>
                    <RefreshIcon sx={{ animation: isRefreshing ? "rotate 1s linear infinite" : "none" }} />
                  </IconButton>
                </Tooltip>

                {activeTab === 0 && (
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.75rem", "&.Mui-focused": { color: "#FDB813" }, "&.MuiInputLabel-shrink": { color: "#FDB813" } }}>Date to Expire</InputLabel>
                    <Select
                      value={expiryFilter}
                      label="Date to Expire"
                      onChange={(e) => setExpiryFilter(e.target.value)}
                      sx={{
                        color: "#fff",
                        borderRadius: "10px",
                        background: "rgba(255,255,255,0.05)",
                        ".MuiOutlinedInput-notchedOutline": { borderColor: "#FDB813" },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FDB813" },
                        fontSize: "0.85rem"
                      }}
                    >
                      <MenuItem value="15">15 Days</MenuItem>
                      <MenuItem value="30">30 Days</MenuItem>
                      <MenuItem value="60">60 Days</MenuItem>
                      <MenuItem value="90">90 Days</MenuItem>
                      <MenuItem value="All">All</MenuItem>
                    </Select>
                  </FormControl>
                )}

                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.75rem", "&.Mui-focused": { color: "#FDB813" }, "&.MuiInputLabel-shrink": { color: "#FDB813" } }}>Search In</InputLabel>
                  <Select
                    value={searchColumn}
                    label="Search In"
                    onChange={(e) => setSearchColumn(e.target.value)}
                    sx={{
                      color: "#fff",
                      borderRadius: "10px",
                      background: "rgba(255,255,255,0.05)",
                      ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.1)" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FDB813" },
                      fontSize: "0.85rem"
                    }}
                  >
                    <MenuItem value="all">All Columns</MenuItem>
                    <MenuItem value="name">Name</MenuItem>
                    <MenuItem value="phone">Phone</MenuItem>
                    <MenuItem value="vehicleNumber">Vehicle No</MenuItem>
                    <MenuItem value="scheduleDate">Schedule Date</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.75rem", "&.Mui-focused": { color: "#FDB813" }, "&.MuiInputLabel-shrink": { color: "#FDB813" } }}>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{
                      color: "#fff",
                      borderRadius: "10px",
                      background: "rgba(255,255,255,0.05)",
                      ".MuiOutlinedInput-notchedOutline": { borderColor: "#FDB813" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FDB813" },
                      fontSize: "0.85rem"
                    }}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="expiring soon">Expiring Soon</MenuItem>
                    <MenuItem value="expired">Expired</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.75rem", "&.Mui-focused": { color: "#FDB813" }, "&.MuiInputLabel-shrink": { color: "#FDB813" } }}>Vehicle</InputLabel>
                  <Select
                    value={vehicleTypeFilter}
                    label="Vehicle"
                    onChange={(e) => setVehicleTypeFilter(e.target.value)}
                    sx={{
                      color: "#fff",
                      borderRadius: "10px",
                      background: "rgba(255,255,255,0.05)",
                      ".MuiOutlinedInput-notchedOutline": { borderColor: "#FDB813" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FDB813" },
                      fontSize: "0.85rem"
                    }}
                  >
                    <MenuItem value="all">All Vehicles</MenuItem>
                    <MenuItem value="two_wheeler">Two Wheeler</MenuItem>
                    <MenuItem value="private_car">Private Car</MenuItem>
                    <MenuItem value="passengers_vehicle">Passengers Vehicle</MenuItem>
                    <MenuItem value="goods_vehicle">Goods Vehicle</MenuItem>
                    <MenuItem value="miscellaneous_vehicle">Miscellaneous Vehicle</MenuItem>
                    <MenuItem value="non_motor">Non - Motor</MenuItem>
                    <MenuItem value="idv_increase">IDV Increase</MenuItem>
                    <MenuItem value="hp_endorsement">HP ENDORSEMENT</MenuItem>
                    <MenuItem value="solar_policy">SOLAR POLICY</MenuItem>
                    <MenuItem value="fire_policy">Fire Policy</MenuItem>
                    <MenuItem value="name_transfer">Name Transfer</MenuItem>
                    <MenuItem value="school_bus">SCHOOL BUS</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "rgba(255, 255, 255, 0.4)", fontSize: "1.1rem" }} /></InputAdornment>,
                    sx: {
                      borderRadius: "10px",
                      background: "rgba(255, 255, 255, 0.05)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      color: "#fff",
                      width: { xs: "100%", md: 240 },
                      fontSize: "0.85rem",
                      "&.Mui-focused": { borderColor: "#FDB813", background: "rgba(255, 255, 255, 0.1)" }
                    }
                  }}
                />

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title="Advanced Filters">
                    <IconButton
                      onClick={(e) => setFilterPopoverAnchor(e.currentTarget)}
                      sx={{
                        borderRadius: "10px",
                        border: "1px solid",
                        borderColor: (filterRating !== 0 || filterVerification !== 'all' || schedStart || schedEnd || endStart || endEnd) ? "#FDB813" : "rgba(255, 255, 255, 0.1)",
                        color: (filterRating !== 0 || filterVerification !== 'all' || schedStart || schedEnd || endStart || endEnd) ? "#FDB813" : "#ffffff",
                      }}
                    >
                      <TuneIcon size="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Export">
                    <IconButton sx={{ borderRadius: "10px", border: "1px solid rgba(255, 255, 255, 0.1)", color: "#FDB813" }}>
                      <ArrowUpward size="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Reset">
                    <IconButton onClick={resetAllFilters} sx={{ borderRadius: "10px", border: "1px solid rgba(255, 255, 255, 0.1)", color: "rgba(255, 255, 255, 0.5)", "&:hover": { color: "#ff4d4d", borderColor: "#ff4d4d" } }}>
                      <FilterOffIcon size="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>

            {/* Summary Status Chips */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 3 }}>
              {[
                { label: "Total", count: filteredEnqUsers.length, color: "rgba(255,255,255,0.1)", textColor: "#fff" },
                { label: "Active", count: filteredEnqUsers.filter(u => getUserAggregateStatus(u) === "Active").length, color: "rgba(76, 175, 80, 0.1)", textColor: "#4caf50" },
                { label: "Expiring", count: filteredEnqUsers.filter(u => getUserAggregateStatus(u) === "Expiring Soon").length, color: "rgba(253, 184, 19, 0.1)", textColor: "#FDB813" },
                { label: "Expired", count: filteredEnqUsers.filter(u => getUserAggregateStatus(u) === "Expired").length, color: "rgba(244, 67, 54, 0.1)", textColor: "#f44336" },
                { label: "Two Wheeler", count: filteredEnqUsers.filter(u => u.vehicles?.some(v => (v.type || "two_wheeler").toLowerCase() === "two_wheeler")).length, icon: <TwoWheeler fontSize="small" /> },
                { label: "Private Car", count: filteredEnqUsers.filter(u => u.vehicles?.some(v => (v.type || "").toLowerCase() === "private_car")).length, icon: <DirectionsCar fontSize="small" /> },
                { label: "Goods Vehicle", count: filteredEnqUsers.filter(u => u.vehicles?.some(v => (v.type || "").toLowerCase() === "goods_vehicle")).length, icon: <LocalShipping fontSize="small" /> },
              ].map((chip, idx) => (
                <Chip
                  key={idx}
                  icon={chip.icon ? React.cloneElement(chip.icon, { style: { color: chip.textColor || "#FDB813", fontSize: "14px", marginLeft: "8px" } }) : null}
                  label={`${chip.label}: ${chip.count}`}
                  sx={{
                    bgcolor: chip.color || "rgba(255,255,255,0.05)",
                    color: chip.textColor || "rgba(255,255,255,0.7)",
                    fontWeight: 700,
                    borderRadius: "8px",
                    border: "1px solid",
                    borderColor: chip.textColor ? alpha(chip.textColor, 0.3) : "rgba(255,255,255,0.1)",
                    "& .MuiChip-label": { px: 1.5 }
                  }}
                />
              ))}
            </Box>

            {/* Active Chips Display */}
            {(statusFilter !== 'all' || vehicleTypeFilter !== 'all' || assignedToFilter !== 'all' || filterRating !== 0 || filterVerification !== 'all' || schedStart || schedEnd || endStart || endEnd) && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                {statusFilter !== 'all' && <Chip label={`Status: ${statusFilter}`} onDelete={() => setStatusFilter('all')} size="small" sx={{ bgcolor: "rgba(253, 184, 19, 0.1)", color: "#FDB813" }} />}
                {vehicleTypeFilter !== 'all' && <Chip label={`Vehicle: ${vehicleTypeFilter}`} onDelete={() => setVehicleTypeFilter('all')} size="small" sx={{ bgcolor: "rgba(253, 184, 19, 0.1)", color: "#FDB813" }} />}
                {assignedToFilter !== 'all' && <Chip label={`Assigned: ${assignedToFilter}`} onDelete={() => setAssignedToFilter('all')} size="small" sx={{ bgcolor: "rgba(253, 184, 19, 0.1)", color: "#FDB813" }} />}
                {filterRating !== 0 && <Chip label={`Rating: ${filterRating}+`} onDelete={() => setFilterRating(0)} size="small" sx={{ bgcolor: "rgba(253, 184, 19, 0.1)", color: "#FDB813" }} />}
                {filterVerification !== 'all' && <Chip label={`Verified: ${filterVerification}`} onDelete={() => setFilterVerification('all')} size="small" sx={{ bgcolor: "rgba(253, 184, 19, 0.1)", color: "#FDB813" }} />}
                {(schedStart || schedEnd) && <Chip label="Schedule Range Active" onDelete={() => { setSchedStart(""); setSchedEnd(""); }} size="small" sx={{ bgcolor: "rgba(253, 184, 19, 0.1)", color: "#FDB813" }} />}
                {(endStart || endEnd) && <Chip label="Expiry Range Active" onDelete={() => { setEndStart(""); setEndEnd(""); }} size="small" sx={{ bgcolor: "rgba(253, 184, 19, 0.1)", color: "#FDB813" }} />}
              </Box>
            )}

            {/* Filter Hub Popover */}
            <Popover
              open={Boolean(filterPopoverAnchor)}
              anchorEl={filterPopoverAnchor}
              onClose={() => setFilterPopoverAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: { p: 3, background: "#1a1a1a", border: "1px solid rgba(253, 184, 19, 0.2)", boxShadow: "0 12px 48px rgba(0,0,0,0.8)", minWidth: 400, borderRadius: "16px" }
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Typography variant="h6" sx={{ color: "#ffffff", fontWeight: 800, fontFamily: "'Outfit', sans-serif" }}>Advanced Filters</Typography>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FormControl size="small" fullWidth>
                      <InputLabel sx={{ color: "rgba(255,255,255,0.6)" }}>Status</InputLabel>
                      <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)} sx={{ color: "#fff", ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" } }}>
                        <MenuItem value="all">All Status</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="expiring soon">Expiring Soon</MenuItem>
                        <MenuItem value="expired">Expired</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl size="small" fullWidth>
                      <InputLabel sx={{ color: "rgba(255,255,255,0.6)" }}>Vehicle</InputLabel>
                      <Select value={vehicleTypeFilter} label="Vehicle" onChange={(e) => setVehicleTypeFilter(e.target.value)} sx={{ color: "#fff", ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" } }}>
                        <MenuItem value="all">All types</MenuItem>
                        <MenuItem value="two_wheeler">Two Wheeler</MenuItem>
                        <MenuItem value="private_car">Private Car</MenuItem>
                        <MenuItem value="passengers_vehicle">Passengers Vehicle</MenuItem>
                        <MenuItem value="goods_vehicle">Goods Vehicle</MenuItem>
                        <MenuItem value="miscellaneous_vehicle">Miscellaneous Vehicle</MenuItem>
                        <MenuItem value="non_motor">Non - Motor</MenuItem>
                        <MenuItem value="idv_increase">IDV Increase</MenuItem>
                        <MenuItem value="hp_endorsement">HP ENDORSEMENT</MenuItem>
                        <MenuItem value="solar_policy">SOLAR POLICY</MenuItem>
                        <MenuItem value="fire_policy">Fire Policy</MenuItem>
                        <MenuItem value="name_transfer">Name Transfer</MenuItem>
                        <MenuItem value="school_bus">SCHOOL BUS</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl size="small" fullWidth>
                      <InputLabel sx={{ color: "rgba(255,255,255,0.6)" }}>Assigned To</InputLabel>
                      <Select value={assignedToFilter} label="Assigned To" onChange={(e) => setAssignedToFilter(e.target.value)} sx={{ color: "#fff", ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" } }}>
                        <MenuItem value="all">All Users</MenuItem>
                        <MenuItem value="unassigned">Unassigned</MenuItem>
                        {assignUsers.map(u => (
                          <MenuItem key={u.id} value={u.userName || u.id}>{u.userName}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl size="small" fullWidth>
                      <InputLabel sx={{ color: "rgba(255,255,255,0.6)" }}>Verified</InputLabel>
                      <Select value={filterVerification} label="Verified" onChange={(e) => setFilterVerification(e.target.value)} sx={{ color: "#fff", ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" } }}>
                        <MenuItem value="all">All Status</MenuItem>
                        <MenuItem value="Yes">Verified Only</MenuItem>
                        <MenuItem value="No">Unverified Only</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Box>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", mb: 1, display: "block" }}>Minimum Rating</Typography>
                  <Rating value={filterRating} onChange={(e, v) => setFilterRating(v)} sx={{ "& .MuiRating-iconFilled": { color: "#FDB813" }, "& .MuiRating-iconEmpty": { color: "rgba(255,255,255,0.1)" } }} />
                </Box>

                <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px" }}>
                  <Typography variant="caption" sx={{ color: "#FDB813", fontWeight: 700, mb: 2, display: "block" }}>SCHEDULE DATE RANGE</Typography>
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    <TextField size="small" type="date" label="From" value={schedStart} onChange={(e) => setSchedStart(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth sx={{ "& input": { color: "#fff" }, "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.5)" } }} />
                    <TextField size="small" type="date" label="To" value={schedEnd} onChange={(e) => setSchedEnd(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth sx={{ "& input": { color: "#fff" }, "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.5)" } }} />
                  </Box>
                </Box>

                <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "12px" }}>
                  <Typography variant="caption" sx={{ color: "#FDB813", fontWeight: 700, mb: 2, display: "block" }}>EXPIRY DATE RANGE</Typography>
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    <TextField size="small" type="date" label="From" value={endStart} onChange={(e) => setEndStart(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth sx={{ "& input": { color: "#fff" }, "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.5)" } }} />
                    <TextField size="small" type="date" label="To" value={endEnd} onChange={(e) => setEndEnd(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth sx={{ "& input": { color: "#fff" }, "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.5)" } }} />
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button fullWidth variant="contained" onClick={() => setFilterPopoverAnchor(null)} sx={{ bgcolor: "#FDB813", color: "#111", fontWeight: 700 }}>Apply</Button>
                  <Button fullWidth variant="outlined" onClick={resetAllFilters} sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.3)" }}>Reset</Button>
                </Box>
              </Box>
            </Popover>



            {/* Responsive Table */}
            <TableContainer sx={{
              borderRadius: "12px",
              border: "1px solid rgba(255,255,255,0.05)",
              overflow: "auto",
              '&::-webkit-scrollbar': { height: '8px' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(253, 184, 19, 0.2)', borderRadius: '4px' }
            }}>
              <Table size="small" sx={{ minWidth: 1200, "& .MuiTableCell-root": { whiteSpace: "nowrap" } }}>
                <TableHead sx={{ bgcolor: "rgba(255,255,255,0.02)" }}>
                  <TableRow>
                    {/* Checkbox column – admin only */}
                    {loggedUserType === "ADMIN" && (
                      <TableCell
                        padding="checkbox"
                        sx={{ borderBottom: "1px solid rgba(253, 184, 19, 0.1)", pl: 1.5 }}
                      >
                        <Checkbox
                          indeterminate={someCurrentSelected}
                          checked={allCurrentSelected}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          sx={{
                            color: "rgba(255,255,255,0.3)",
                            "&.Mui-checked": { color: "#FDB813" },
                            "&.MuiCheckbox-indeterminate": { color: "#FDB813" }
                          }}
                        />
                      </TableCell>
                    )}
                    {["S.No", "Customer", "Contact", "Vehicle", "Schedule", "Expiry", "Assigned", "Rating", "Verified", "Files", "Actions"].map(h => (
                      <TableCell key={h} sx={{ color: "#ffffff", fontWeight: 800, borderBottom: "1px solid rgba(253, 184, 19, 0.1)", py: 2 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={loggedUserType === "ADMIN" ? 12 : 11} align="center" sx={{ py: 10 }}>
                        <img src={nodatafound} alt="No Data" style={{ opacity: 0.5, maxWidth: 200 }} />
                        <Typography sx={{ color: "rgba(255,255,255,0.4)", mt: 2 }}>No customers match your filters</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedUsers.map((u, i) => (
                      <TableRow
                        key={u.id || i}
                        hover
                        selected={isRowSelected(u.id)}
                        sx={{
                          "&:hover": { bgcolor: "rgba(253, 184, 19, 0.02) !important" },
                          ...(isRowSelected(u.id) && {
                            bgcolor: "rgba(253, 184, 19, 0.05) !important",
                            outline: "1px solid rgba(253, 184, 19, 0.2)"
                          })
                        }}
                      >
                        {/* Row checkbox – admin only */}
                        {loggedUserType === "ADMIN" && (
                          <TableCell padding="checkbox" sx={{ pl: 1.5 }}>
                            <Checkbox
                              checked={isRowSelected(u.id)}
                              onChange={() => handleToggleRow(u.id)}
                              sx={{
                                color: "rgba(255,255,255,0.3)",
                                "&.Mui-checked": { color: "#FDB813" }
                              }}
                            />
                          </TableCell>
                        )}
                        <TableCell sx={{ color: "rgba(255,255,255,0.6)" }}>{page * 5 + i + 1}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Avatar sx={{ bgcolor: "#FDB813", color: "#111", fontWeight: 700, width: 32, height: 32, fontSize: "0.85rem" }}>
                              {(u.name || "?").charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ color: "#fff", fontWeight: 600 }}>{u.name || "--"}</Typography>
                              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)" }}>{u.location || "No Location"}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: "#fff" }}>{u.phone || "--"}</Typography>
                          <Typography variant="caption" sx={{ color: "#FDB813", cursor: "pointer" }} onClick={() => u.email && navigator.clipboard.writeText(u.email)}>{u.email || "No Email"}</Typography>
                        </TableCell>
                        <TableCell>
                          {u.vehicles && u.vehicles.length > 0 ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              {getVehicleIcon(u.vehicles[0].type)}
                              <Typography variant="body2" sx={{ color: "#fff" }}>{u.vehicles[0].number || "--"}</Typography>
                              {u.vehicles.length > 1 && <Chip label={`+${u.vehicles.length - 1}`} size="small" sx={{ height: 16, fontSize: '0.65rem' }} />}
                            </Box>
                          ) : "--"}
                        </TableCell>
                        <TableCell sx={{ color: "rgba(255,255,255,0.7)" }}>{formatDate(u.scheduleDate || (u.vehicles && u.vehicles[0]?.scheduleDate))}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: getUserAggregateColor(u) === 'error' ? '#ff4d4d' : '#fff' }}>
                            {formatDate(u.endDate || (u.vehicles && u.vehicles[0]?.endDate))}
                          </Typography>
                          <Chip label={getUserAggregateStatus(u)} color={getUserAggregateColor(u)} size="small" sx={{ height: 16, fontSize: '0.65rem', mt: 0.5 }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
                            {u.assignedToWho || "--"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Rating value={u.rating || 0} readOnly size="small" sx={{ "& .MuiRating-iconFilled": { color: "#FDB813" } }} />
                        </TableCell>
                        <TableCell>
                          {u.verification === "Yes" ? (
                            <Tooltip title={`Verified by ${u.verifiedBy || 'Staff'}`}>
                              <VerifiedIcon sx={{ color: "#FDB813", fontSize: "1.2rem" }} />
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.3)" }}>Unverified</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {u.documents?.length > 0 ? (
                            <IconButton size="small" onClick={() => handleOpenFiles(u.documents)} sx={{ color: "#FDB813" }}>
                              <DescriptionIcon fontSize="small" />
                            </IconButton>
                          ) : "--"}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <IconButton size="small" onClick={() => handleViewDetails(u)} sx={{ color: "rgba(255,255,255,0.6)" }}><Visibility fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => handleEditUser(u)} sx={{ color: "rgba(255,255,255,0.6)" }}><EditIcon fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => handleOpenDelete(u.id)} sx={{ color: "#ff4d4d" }}><DeleteIcon fontSize="small" /></IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* ── Admin Assignment Footer ───────────────────────────────── */}
            {loggedUserType === "ADMIN" && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: selectedRows.size > 0 ? "rgba(253, 184, 19, 0.35)" : "rgba(255,255,255,0.06)",
                  background: selectedRows.size > 0
                    ? "rgba(253, 184, 19, 0.06)"
                    : "rgba(255,255,255,0.02)",
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                  transition: "all 0.3s ease"
                }}
              >
                {/* Selection count badge */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PersonAddIcon sx={{ color: selectedRows.size > 0 ? "#FDB813" : "rgba(255,255,255,0.2)", fontSize: "1.3rem", transition: "color 0.3s" }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: selectedRows.size > 0 ? "#FDB813" : "rgba(255,255,255,0.3)",
                      fontWeight: 600,
                      fontFamily: "'Poppins', sans-serif",
                      transition: "color 0.3s",
                      minWidth: 120
                    }}
                  >
                    {selectedRows.size > 0
                      ? `${selectedRows.size} customer${selectedRows.size > 1 ? "s" : ""} selected`
                      : "No rows selected"}
                  </Typography>
                </Box>

                {/* Assign-to user dropdown */}
                <FormControl
                  size="small"
                  disabled={selectedRows.size === 0}
                  sx={{ minWidth: 220, flex: 1, maxWidth: 320 }}
                >
                  <InputLabel
                    sx={{
                      color: selectedRows.size > 0 ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.2)",
                      fontSize: "0.82rem",
                      "&.Mui-focused": { color: "#FDB813" },
                      "&.MuiInputLabel-shrink": { color: selectedRows.size > 0 ? "#FDB813" : "rgba(255,255,255,0.2)" }
                    }}
                  >
                    Assign To
                  </InputLabel>
                  <Select
                    value={selectedAssignUser}
                    label="Assign To"
                    onChange={(e) => setSelectedAssignUser(e.target.value)}
                    sx={{
                      color: "#fff",
                      borderRadius: "10px",
                      background: selectedRows.size > 0
                        ? "rgba(255,255,255,0.07)"
                        : "rgba(255,255,255,0.02)",
                      ".MuiOutlinedInput-notchedOutline": {
                        borderColor: selectedRows.size > 0
                          ? "rgba(253, 184, 19, 0.5)"
                          : "rgba(255,255,255,0.08)"
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FDB813" },
                      "&.Mui-disabled": {
                        ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.06)" }
                      },
                      fontSize: "0.85rem"
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          background: "#1a1a1a",
                          border: "1px solid rgba(253,184,19,0.15)",
                          borderRadius: "10px",
                          mt: 0.5
                        }
                      }
                    }}
                  >
                    {assignUsers.length === 0 ? (
                      <MenuItem disabled>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)" }}>No staff available</Typography>
                      </MenuItem>
                    ) : (
                      assignUsers.map((au) => (
                        <MenuItem
                          key={au.id}
                          value={au.id}
                          sx={{
                            color: "#fff",
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: "0.85rem",
                            "&:hover": { bgcolor: "rgba(253,184,19,0.08)" },
                            "&.Mui-selected": { bgcolor: "rgba(253,184,19,0.12) !important" }
                          }}
                        >
                          <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#fff" }}>
                              {au.userName.toUpperCase()}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                {/* Assign It button */}
                <Button
                  variant="contained"
                  disabled={selectedRows.size === 0 || !selectedAssignUser || assignLoading}
                  onClick={handleAssignCustomers}
                  startIcon={
                    assignLoading
                      ? <CircularProgress size={16} sx={{ color: "#111" }} />
                      : <PersonAddIcon />
                  }
                  sx={{
                    bgcolor: "#FDB813",
                    color: "#111",
                    fontWeight: 700,
                    fontFamily: "'Poppins', sans-serif",
                    borderRadius: "10px",
                    px: 3,
                    py: 1,
                    whiteSpace: "nowrap",
                    boxShadow: selectedRows.size > 0 && selectedAssignUser
                      ? "0 4px 20px rgba(253, 184, 19, 0.35)"
                      : "none",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      bgcolor: "#e5a800",
                      boxShadow: "0 6px 24px rgba(253, 184, 19, 0.5)"
                    },
                    "&.Mui-disabled": {
                      bgcolor: "rgba(255,255,255,0.08)",
                      color: "rgba(255,255,255,0.2)"
                    }
                  }}
                >
                  {assignLoading ? "Assigning..." : "Assign It"}
                </Button>
              </Box>
            )}

            {/* Pagination Row */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3, flexWrap: "wrap", gap: 2 }}>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>
                Showing {displayedUsers.length} of {sortedUsers.length}
              </Typography>
              <Pagination
                page={page + 1}
                count={Math.ceil(sortedUsers.length / 5)}
                onChange={(e, v) => setPage(v - 1)}
                size="small"
                sx={{ "& .MuiPaginationItem-root": { color: "#fff", borderColor: "rgba(255,255,255,0.1)" }, "& .Mui-selected": { bgcolor: "#FDB813 !important", color: "#111" } }}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Dialogs & Overlays */}
      <CustomerDetailsView
        open={viewDetailsDialog}
        onClose={() => setViewDetailsDialog(false)}
        user={selectedUser}
        getUserStatus={getUserStatus}
        handleEditUser={handleEditUser}
        handleFilePreview={handlePreview}
        loggedUserType={loggedUserType}
      />

      <Dialog open={openFileDialog} onClose={handleCloseFiles} fullWidth maxWidth="xs" PaperProps={{ sx: { background: "#1a1a1a", border: "1px solid rgba(253,184,19,0.1)", borderRadius: "16px" } }}>
        <DialogTitle sx={{ color: "#fff", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>Documents</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <List>
            {selectedFiles.map((file, idx) => (
              <ListItem key={idx} onClick={() => handlePreview(file)} sx={{ cursor: "pointer", borderRadius: "8px", "&:hover": { bgcolor: "rgba(255,255,255,0.05)" } }}>
                <ListItemIcon><DescriptionIcon sx={{ color: "#FDB813" }} /></ListItemIcon>
                <ListItemText primary={file.name} sx={{ color: "#fff" }} secondary={`Type: ${file.type}`} secondaryTypographyProps={{ color: "rgba(255,255,255,0.5)" }} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions><Button onClick={handleCloseFiles} sx={{ color: "#FDB813" }}>Close</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(fileViewer)} onClose={handleCloseFileViewer} maxWidth="lg" fullWidth PaperProps={{ sx: { height: "90vh", background: "#000", border: "1px solid rgba(253,184,19,0.2)" } }}>
        <DialogTitle sx={{ bgcolor: "#111", color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {fileViewer?.name}
          <Box>
            <IconButton onClick={handleDownloadFile} sx={{ color: "#FDB813" }}><i className="bi bi-download"></i></IconButton>
            <IconButton onClick={handleCloseFileViewer} sx={{ color: "#fff" }}>✕</IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: "flex", justifyContent: "center", alignItems: "center", bgcolor: "#000" }}>
          {fileViewer?.type?.startsWith("image") ? (
            <img src={fileViewer.url} alt="Preview" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
          ) : (
            <iframe src={fileViewer?.url} title="PDF Preview" style={{ width: "100%", height: "100%", border: "none" }} />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={handleCloseDelete} PaperProps={{ sx: { background: "#1a1a1a", border: "1px solid rgba(255,77,77,0.2)", borderRadius: "16px" } }}>
        <DialogTitle sx={{ color: "#ff4d4d" }}>Confirm Delete</DialogTitle>
        <DialogContent><Typography sx={{ color: "#fff" }}>Remove this customer permanently? This cannot be undone.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} sx={{ color: "rgba(255,255,255,0.6)" }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => { handleDeleteUser(deleteUserId); handleCloseDelete(); }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}